import React, {
  Component,
  PropTypes,
  createElement,
  cloneElement,
} from 'react'
import classNames from 'classnames'
import { darker } from '../../helpers/color'
import Icon from '../Icon'
import Loading from '../Loading'

import styles from './style.styl'
export const SIZES = {
  small: styles['Button--small'],
  medium: styles['Button--medium'],
  large: styles['Button--large'],
}

export const ROLES = {
  primary: styles['Button--primary'],
  secondary: styles['Button--secondary'],
  danger: styles['Button--danger'],
  blue: styles['Button--blue'], // not a role, but here for legacy purposes
}

const iconColors = {
  filled: {
    blue: { normal: 'white', hover: 'white' },
    primary: { normal: 'white', hover: 'white' },
    secondary: { normal: 'white', hover: 'white' },
    danger: { normal: 'white', hover: 'white' },
  },
  hollow: {
    blue: { normal: '#0176FF', hover: '#0176FF' },
    primary: { normal: '#13CB6E', hover: '#1FA45C' },
    secondary: { normal: '#9A9A9A', hover: '#0176FF' },
    danger: { normal: '#E92E4B', hover: '#0176FF' },
  },
}

const iconSizes = {
  small: 20,
  medium: 25,
  large: 30,
}

const iconSides = {
  left: styles['Button__icon--left'],
  right: styles['Button__icon--right'],
}

const focusBlurRadius = '4px'

class Button extends Component {
  state = {
    hover: false,
    focus: false,
  }

  getBrandingStyle() {
    const { brandPrimary, brandSecondary, hollow } = this.props
    let brandingStyles = {}
    if (brandPrimary && brandSecondary) {
      const brandPrimaryDarker = darker(brandPrimary, 0.10)
      brandingStyles = {
        backgroundColor: brandPrimary,
        borderColor: brandPrimary,
        color: brandSecondary,
      }
      if (this.state.hover) {
        brandingStyles = {
          ...brandingStyles,
          backgroundColor: brandPrimaryDarker,
          borderColor: brandPrimaryDarker,
        }
      }
      if (this.state.focus) {
        brandingStyles = {
          ...brandingStyles,
          borderColor: brandPrimaryDarker,
          boxShadow: `0 0 ${focusBlurRadius} 0 ${brandPrimaryDarker}`,
        }
      }
      if (hollow) {
        brandingStyles.color = brandPrimaryDarker
        brandingStyles.backgroundColor = '#FFF'

        if (this.state.hover) {
          brandingStyles = {
            ...brandingStyles,
            backgroundColor: '#FFF',
          }
        }
      }
    }
    return brandingStyles
  }

  /* Render an icon. If it's a string, it's the icon source. We render that.
   * If it's an Icon component, clone it with the color prop provided.
   */
  renderIcon(icon, color, size, iconSide) {
    if (_.isString(icon)) {
      return (
        <Icon
          src={icon}
          className={iconSides[iconSide]}
          color={color}
          size={size} />
      )
    }
    return cloneElement(icon, {
      color,
      className: iconSides[iconSide],
    })
  }

  render () {
    const {
      children,
      className,
      href,
      style,
      size,
      hollow,
      role,
      cta,
      disabled,
      block,
      link,
      loading,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onFocus,
      onBlur,
      element,
      iconLeft,
      iconRight,
      iconSize,
      ...restProps,
    } = this.props

    // branding styles

    const brandingStyles = this.getBrandingStyle()

    const ComponentClass = href ? 'a' : 'button'
    const finalClassName = classNames(
      styles.Button,
      SIZES[size],
      ROLES[role],
      className, {
        [styles['Button--hollow']]: hollow,
        [styles['Button--cta']]: cta,
        [styles['Button--disabled']]: disabled,
        [styles['Button--block']]: block,
        [styles['Button--link']]: link,
        [styles['Button--loading']]: loading,
      }
    )

    const elementProps = {
      className: finalClassName,
      onClick: !loading ? onClick : null,
      onMouseEnter: () => {
        this.setState({ hover: true })
        if (onMouseEnter) {
          onMouseEnter.bind(this)
        }
      },
      onMouseLeave: () => {
        this.setState({ hover: false })
        if (onMouseLeave) {
          onMouseLeave.bind(this)
        }
      },
      onFocus: () => {
        this.setState({ focus: true })
        if (onFocus) {
          onFocus.bind(this)
        }
      },
      onBlur: () => {
        this.setState({ focus: false })
        if (onBlur) {
          onBlur.bind(this)
        }
      },
      style: {
        ...style,
        ...brandingStyles,
      },
    }

    let inner = children
    const fillStyle = hollow ? 'hollow' : 'filled'
    const normalColor = iconColors[fillStyle][role].normal
    const hoverColor = iconColors[fillStyle][role].hover
    const iconColor = this.state.hover ? hoverColor : normalColor
    if (loading) {
      inner = <Loading color={iconColor} />
    } else if (iconLeft || iconRight) {
      const iconSize = iconSize ? iconSize : iconSizes[size]

      inner = (
        <span className="flex">
          {iconLeft ? this.renderIcon(iconLeft, iconColor, iconSize, 'left') : null}
          {children}
          {iconRight ? this.renderIcon(iconRight, iconColor, iconSize, 'right') : null}
        </span>
      )
    }

    if (element) {
      delete restProps.brandSecondary
      return createElement(element, { ...elementProps, ...restProps }, inner)
    }

    if (href) {
      delete restProps.brandSecondary
      return (
        <a href={href} {...elementProps} {...restProps}>
          {inner}
        </a>
      )
    }

    return (
      <button role="button" {...elementProps} {...restProps}>
        {inner}
      </button>
    )
  }
}

Button.propTypes = {
  /** Optional additional className */
  className: PropTypes.string,
  /** Children */
  children: PropTypes.any,
  /** If provided, the button will use a <a> element */
  href: PropTypes.string,
  /** Function to call when button is clicked */
  onClick: PropTypes.func,
  /** Primary branding color */
  brandPrimary: PropTypes.string,
  /** Secondary branding color */
  brandSecondary: PropTypes.string,
  /** Optional style object */
  style: PropTypes.object,
  /** Enables hollow button style */
  hollow: PropTypes.bool,
  /** Changes button color */
  role: PropTypes.string,
  /** Changes button size */
  size: PropTypes.string,
  /** Capitalizies button */
  cta: PropTypes.bool,
  /** Shows a loading sequence */
  loading: PropTypes.bool,
  /** Override which element to use */
  element: PropTypes.any,
  /** Function to call when mouse enters button */
  onMouseEnter: PropTypes.func,
  /** Function to call when mouse leaves button */
  onMouseLeave: PropTypes.func,
  /** Function to call when focusing on button */
  onFocus: PropTypes.func,
  /** Function to call when removing focus */
  onBlur: PropTypes.func,
  /** Icon SVG string (left) */
  iconleft: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
  /** Icon SVG string (right) */
  iconRight: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.node,
  ]),
}
Button.defaultProps = {
  style: {},
  size: 'medium',
  role: 'primary',
  cta: false,
  hollow: false,
  brandSecondary: 'white',
}

export default Button



// WEBPACK FOOTER //
// ./node_modules/shared-web/src/components/Button/index.js